
import { Component } from 'vue-property-decorator';
import StackedBarChartFilterable from '@/components/reports-v3/components/filterables/StackedBarChartFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { PermissionsGroup } from '@/store/models.def';
import gdbx from '@/store/modules/gdbx';
import * as echarts from 'echarts';
import { GobiColor } from '@/helpers/color';
import moment from 'moment';
import DateRangeFilter from '@/components/reports-v3/components/filters/DateRangeFilter.vue';
import { COL, getTableName } from '@/worker/db/sqlHelpers';

@Component({
	components: {
		StackedBarChartFilterable,
		DateRangeFilter,
	},
})
export default class SalesQuotationConversionRateByDocumentBarChart extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['sales'];
	}

	public filterIds: Array<
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
        | 'areas'
	> = ['agents', 'customers', 'areas'];

	public manualDateRange: [number, number] = [
		moment().add(-3, 'month').valueOf(),
		moment().valueOf(),
	];

	public xAxisData: string[] = [];
	public finalDatas: Array<{
		name: string;
		openAmount: number;
		convertedAmount: number;
		totalAmount: number;
		conversionRate: number;
	}> = [];
	public legends: string[] = [];
	public series: echarts.ECharts[] | any = [];

	public get currency() {
		return gdbx.currencySymbol;
	}

	public get expensiveHook() {
		const {
            manualDateRange,
            selectedAgents,
            selectedAreas,
            selectedCustomers,
        } = this;
		return JSON.stringify([
            manualDateRange,
			selectedAgents,
            selectedAreas,
            selectedCustomers,
		]);
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.3);

		const quotationsTable = getTableName('quotations');

		// Ensure all cross-table dependencies are loaded
		const queryHead = await this._ensureTablesLoaded(['quotations', 'globalCustomers']);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.3, 0.9);

		// Build filter clauses manually
		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const dateFilter = `TRY_CAST(${COL.DOCDATE} AS BIGINT) BETWEEN ${this.manualDateRange[0]} AND ${this.manualDateRange[1]}`;

		let agentFilter = '';
		if (this.selectedAgents.length > 0) {
			const list = this.selectedAgents.map((a) => `'${escapeStr(a)}'`).join(',');
			agentFilter = ` AND ${COL.AGENT} IN (${list})`;
		}

		const globalCustomersTable = getTableName('globalCustomers');
		let filterClause = '';

		if (this.selectedAreas.length > 0) {
			const areaList = this.selectedAreas.map((a) => `'${escapeStr(a)}'`).join(',');
			filterClause += ` AND ${COL.CODE} IN (
				SELECT "CODE" FROM ${globalCustomersTable} WHERE "AREA" IN (${areaList})
			)`;
		}

		if (this.selectedCustomers.length > 0) {
			const customerList = this.selectedCustomers.map((c) => `'${escapeStr(c)}'`).join(',');
			filterClause += ` AND ${COL.CODE} IN (${customerList})`;
		}

		// Single query: amounts and document conversion rate per agent
		const data = await queryHead.rawQuery(`
			SELECT ${COL.AGENT} as agent,
				SUM(TRY_CAST(${COL.AMOUNT} AS DOUBLE)) as totalAmt,
				SUM(TRY_CAST("OUTSTANDINGAMOUNT" AS DOUBLE)) as osAmt,
				SUM(TRY_CAST(${COL.AMOUNT} AS DOUBLE)
					- TRY_CAST("OUTSTANDINGAMOUNT" AS DOUBLE)) as convertedAmt,
				COUNT(DISTINCT ${COL.DOCNO}) as totalDocs,
				COUNT(DISTINCT CASE
					WHEN TRY_CAST("OSQTY" AS DOUBLE)
						!= TRY_CAST(${COL.QUANTITY} AS DOUBLE)
					THEN ${COL.DOCNO} END) as convertedDocs
			FROM ${quotationsTable}
			WHERE ${dateFilter}${agentFilter}${filterClause}
			GROUP BY ${COL.AGENT}
		`);

		this.generateLoadingText(0.95);

		this.finalDatas = data
			.map((row: any) => ({
				name: String(row.agent),
				openAmount: Number(row.osAmt) || 0,
				convertedAmount: Number(row.convertedAmt) || 0,
				totalAmount: Number(row.totalAmt) || 0,
				conversionRate: Number(row.totalDocs)
					? (Number(row.convertedDocs) / Number(row.totalDocs)) * 100
					: 0,
			}))
			.filter((item) => item.totalAmount > 0);

		this.xAxisData = this.finalDatas
			.sort((a, b) => b.conversionRate - a.conversionRate)
			.map((item) => item.name);

		const outstanding = {
			name: 'Open Quotation',
			type: 'bar',
			barWidth: '40%',
			color: GobiColor.COLOR1,
			stack: 'a',
			data: this.finalDatas.map((dd) => dd.openAmount),
		};

		const converted = {
			name: 'Converted Value',
			type: 'bar',
			barWidth: '40%',
			color: GobiColor.COLOR2,
			stack: 'a',
			data: this.finalDatas.map((dd) => dd.convertedAmount),
		};

		const conversionRates = {
			name: 'Conversion Rate (%)',
			type: 'bar',
			barWidth: '0%',
			color: GobiColor.COLOR3,
			itemStyle: {
				opacity: 0,
				color: false,
			},
			data: this.finalDatas.map((item) => item.conversionRate),
		};

		const total = {
			name: 'Total',
			type: 'bar',
			barWidth: '10%',
			color: GobiColor.COLOR4,
			data: this.finalDatas.map((dd) => dd.totalAmount),
		};

		this.generateLoadingText(1);

		this.series = [converted, outstanding, total, conversionRates];

		this.legends = this.series.map(
			(serie) => serie.name,
		).slice(0, this.series.length - 1);

		this.saveHistory('series', 'xAxisData', 'finalDatas');
	}
}
